/**
 * 道路網組裝櫃 — 讀 sovereign_road_network_manifest，按需載入 TDX 圖層
 */
import { SovereignCabinet } from '../../core/cabinet-base.js';
import { searchGisPlaces } from '../../utils/gis-place-search.js';

const MANIFEST_URL = '/api/live/roads/manifest';
const CLASS6_ID = 'tdx:class:6';

const LAYER_TOGGLES = [
    { id: 'toggleRoad0', classId: 'tdx:class:0', color: '#e85d04' },
    { id: 'toggleRoad1', classId: 'tdx:class:1', color: '#f4a261' },
    { id: 'toggleRoad2', classId: 'tdx:class:2', color: '#2a9d8f' },
    { id: 'toggleRoad3', classId: 'tdx:class:3', color: '#ffd166' },
    { id: 'toggleRoad4', classId: 'tdx:class:4', color: '#90be6d' },
    { id: 'toggleRoad5', classId: 'tdx:class:5', color: '#43aa8b' },
    { id: 'toggleRoad6', classId: CLASS6_ID, color: '#94a3b8', mode: 'bbox' }
];

function escapeHtml(s) {
    return String(s || '').replace(/[<>&]/g, '');
}

class RoadsCabinet extends SovereignCabinet {
    constructor() {
        super();
        this.activeLayers = new Map();
        this.manifest = null;
        this._bboxTimer = null;
        this._bboxGen = 0;
        this.loadAssets('/js/cabinets/roads');
    }

    onMount() {
        for (const { id } of LAYER_TOGGLES) {
            const el = this.shadowRoot.getElementById(id);
            if (el) el.addEventListener('change', () => this.onToggleLayer());
        }
        this.loadManifest();
        this.bindSearch();
    }

    bindSearch() {
        const input = this.shadowRoot.getElementById('roadSearch');
        const box = this.shadowRoot.getElementById('roadSearchHits');
        if (!input || !box) return;
        let timer = null;
        input.addEventListener('input', () => {
            clearTimeout(timer);
            timer = setTimeout(() => this.runSearch(input.value, box), 140);
        });
    }

    async runSearch(raw, box) {
        const q = String(raw || '').trim();
        if (!q) {
            box.hidden = true;
            box.innerHTML = '';
            return;
        }
        const { hits } = await searchGisPlaces(q, { kinds: 'road', limit: 20 });
        if (!hits.length) {
            box.hidden = false;
            box.innerHTML = '<div class="gis-search-empty">沒有符合的路名</div>';
            return;
        }
        box.hidden = false;
        box.innerHTML = hits.map((h) => `
            <button type="button" class="gis-search-hit">
                <b>${escapeHtml(h.name)}</b>
                <div style="font-size:11px;color:#94a3b8">${escapeHtml(h.sub)}</div>
            </button>`).join('');
        box.querySelectorAll('.gis-search-hit').forEach((btn, i) => {
            btn.addEventListener('click', () => {
                const hit = hits[i];
                box.hidden = true;
                this.setStatus(`路名：${hit.name}（${hit.sub || ''}）`);
            });
        });
    }

    setStatus(text) {
        const el = this.shadowRoot.getElementById('road-status');
        if (el) el.textContent = text;
    }

    async loadManifest() {
        try {
            const res = await fetch(MANIFEST_URL, { credentials: 'include' });
            if (!res.ok) throw new Error('manifest 載入失敗');
            this.manifest = await res.json();
            const attr = this.shadowRoot.getElementById('road-attribution');
            if (attr) {
                attr.textContent =
                    this.manifest.attribution_notice ||
                    '道路圖層含 TDX／OSM，popup 顯示 h_attribution';
            }
            this.setStatus(`manifest · ${this.manifest.total_layers} 圖層`);
        } catch (e) {
            console.error('[RoadsCabinet]', e);
            this.setStatus('❌ manifest 失敗');
        }
    }

    layerEntry(classId) {
        return (this.manifest?.layer_index || []).find((l) => l.layer_id === classId);
    }

    toggleMeta(classId) {
        return LAYER_TOGGLES.find((t) => t.classId === classId);
    }

    async onToggleLayer() {
        if (!this.map || !this.manifest) return;
        for (const { id, classId, color, mode } of LAYER_TOGGLES) {
            const checked = this.shadowRoot.getElementById(id)?.checked;
            if (checked && !this.activeLayers.has(classId)) {
                if (mode === 'bbox') await this.reloadBboxLayer(classId, color);
                else await this.loadLayer(classId, color);
            } else if (!checked && this.activeLayers.has(classId)) {
                this.map.removeLayer(this.activeLayers.get(classId));
                this.activeLayers.delete(classId);
            }
        }
        this.setStatus(`已載入 ${this.activeLayers.size} 個道路圖層`);
    }

    bindPopup(feature, lyr, entry) {
        const p = feature.properties || {};
        const attr = p.h_attribution_text
            || p.h_attribution?.attribution_text
            || entry?.h_attribution?.attribution_text
            || '';
        const name = p.h_road_name || p.h_link_id || entry?.layer_id || '';
        lyr.bindPopup(
            `<div style="font-size:11px;"><b>${escapeHtml(name)}</b>${attr ? `<br/>${escapeHtml(attr)}` : ''}</div>`
        );
    }

    canvasRenderer() {
        if (!this.map._sovereignLineCanvas && typeof L?.canvas === 'function') {
            this.map._sovereignLineCanvas = L.canvas({ padding: 0.5 });
        }
        return this.map._sovereignLineCanvas;
    }

    async loadLayer(classId, color) {
        const entry = this.layerEntry(classId);
        if (!entry?.path) return;
        this.setStatus(`載入 ${entry.layer_id}…`);
        try {
            const res = await fetch(`/api/live/roads/layer?id=${encodeURIComponent(classId)}`, {
                credentials: 'include'
            });
            if (!res.ok) throw new Error(entry.path);
            const geojson = await res.json();
            const layer = L.geoJSON(geojson, {
                style: { color, weight: 2, opacity: 0.75 },
                renderer: this.canvasRenderer(),
                onEachFeature: (feature, lyr) => this.bindPopup(feature, lyr, entry)
            }).addTo(this.map);
            this.activeLayers.set(classId, layer);
        } catch (e) {
            console.error('[RoadsCabinet] load', classId, e);
            this.setStatus(`❌ ${classId}`);
        }
    }

    scheduleBboxReload() {
        if (!this.activeLayers.has(CLASS6_ID)) return;
        clearTimeout(this._bboxTimer);
        this._bboxTimer = setTimeout(() => {
            const meta = this.toggleMeta(CLASS6_ID);
            this.reloadBboxLayer(CLASS6_ID, meta?.color || '#94a3b8').catch((e) => {
                console.warn('[RoadsCabinet] bbox reload', e);
            });
        }, 350);
    }

    async reloadBboxLayer(classId, color) {
        if (!this.map) return;
        const entry = this.layerEntry(classId);
        const bounds = this.map.getBounds();
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        const q = new URLSearchParams({
            id: classId,
            xmin: String(sw.lng),
            ymin: String(sw.lat),
            xmax: String(ne.lng),
            ymax: String(ne.lat),
            zoom: String(this.map.getZoom())
        });
        const gen = ++this._bboxGen;
        this.setStatus('載入視窗市區道路…');
        const res = await fetch(`/api/live/roads/bbox?${q}`, { credentials: 'include' });
        if (gen !== this._bboxGen) return;
        if (!res.ok) {
            const body = await res.json().catch(() => ({}));
            this.setStatus(`❌ ${body.error || classId}`);
            return;
        }
        const geojson = await res.json();
        if (this.activeLayers.has(classId)) {
            this.map.removeLayer(this.activeLayers.get(classId));
            this.activeLayers.delete(classId);
        }
        const layer = L.geoJSON(geojson, {
            style: { color, weight: 1.6, opacity: 0.7 },
            renderer: this.canvasRenderer(),
            onEachFeature: (feature, lyr) => this.bindPopup(feature, lyr, entry)
        }).addTo(this.map);
        this.activeLayers.set(classId, layer);
        const st = geojson._stats || {};
        this.setStatus(
            st.capped
                ? `市區道路 ${st.returned} 段（視窗 ${st.totalInBox}，已截斷 · ${st.scale_band || ''}）`
                : `市區道路 ${st.returned} 段（${st.scale_band || ''}）`
        );
    }

    onMapReady(map) {
        this.map = map;
        this._onViewport = () => this.scheduleBboxReload();
        this.map.on('moveend', this._onViewport);
        this.map.on('zoomend', this._onViewport);
    }

    onUnmount() {
        if (this.map && this._onViewport) {
            this.map.off('moveend', this._onViewport);
            this.map.off('zoomend', this._onViewport);
        }
        clearTimeout(this._bboxTimer);
        this.clearLayers();
    }

    clearLayers() {
        if (!this.map) return;
        for (const layer of this.activeLayers.values()) {
            this.map.removeLayer(layer);
        }
        this.activeLayers.clear();
    }
}

customElements.define('roads-cabinet', RoadsCabinet);

function dock() {
    if (window.appCore) {
        const instance = document.createElement('roads-cabinet');
        const container = document.getElementById('socket-roads');
        if (container) {
            container.innerHTML = '';
            container.appendChild(instance);
            window.appCore.registerCabinet('roads', instance);
        }
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', dock);
} else {
    dock();
}
